import type { FC, ReactNode } from 'react'
import type { ServiceId } from '@/types'

import {
    ArrowRight,
    BadgeCheck,
    Check,
    Clock,
    ShieldCheck
} from 'lucide-react'

import { Button, ButtonLink } from '@/components/generals/button'
import { Container } from '@/components/generals/container'
import { Reveal } from '@/components/generals/reveal'
import { SectionLabel } from '@/components/generals/section-label'
import { formatEuro } from '@/lib/format'
import { HOURLY_RATE_TTC, getFromPrice, getService } from '@/lib/pricing'

import { garage } from '@/constants/garage'
import { PRICE_FAMILIES } from '@/constants/pricing'

interface PricingSectionProps {
    onEstimate: (id: ServiceId) => void
}

const GUARANTEES = [
    {
        icon: ShieldCheck,
        title: 'Le prix du devis est le prix payé',
        text: 'Si nous découvrons autre chose en cours d’intervention, on vous appelle avant d’y toucher, photo à l’appui. Vous décidez.'
    },
    {
        icon: BadgeCheck,
        title: 'Garantie constructeur préservée',
        text: 'Entretien selon votre carnet, pièces d’origine ou de qualité équivalente : c’est votre droit, garanti par le règlement européen 461/2010.'
    }
]

export const PricingSection: FC<PricingSectionProps> = ({ onEstimate }): ReactNode => {
    return (
        <section id='tarifs' aria-labelledby='tarifs-title' className='py-20 lg:py-32'>
            <Container className='grid gap-12 lg:grid-cols-12 lg:gap-10 xl:gap-16'>
                <div className='lg:col-span-4'>
                    <div className='lg:sticky lg:top-32'>
                        <SectionLabel index='01'>Tarifs</SectionLabel>

                        <h2 id='tarifs-title' className='heading mt-5 text-title'>
                            Des prix affichés avant d’ouvrir le capot.
                        </h2>

                        <p className='mt-6 text-muted-foreground'>
                            Prix TTC, pièces et main-d’œuvre comprises, à partir d’une citadine. Le montant exact pour votre voiture est confirmé par écrit avant toute intervention.
                        </p>

                        <dl className='mt-8 border-y border-border py-5'>
                            <dt className='label-mono text-muted-foreground'>
                                Taux horaire affiché
                            </dt>

                            <dd className='mt-2 flex flex-wrap items-baseline gap-x-2'>
                                <span className='heading text-[2rem] leading-none tabular-nums'>
                                    {formatEuro(garage.hourlyRate)}
                                </span>

                                <span className='text-sm text-muted-foreground'>
                                    HT, soit {formatEuro(HOURLY_RATE_TTC, true)} TTC
                                </span>
                            </dd>

                            <dd className='mt-2 text-sm text-muted-foreground'>
                                En concession, comptez souvent 90 à 120&nbsp;€ HT de l’heure.
                            </dd>
                        </dl>

                        <ButtonLink href='#devis' variant='secondary' className='mt-8'>
                            Estimer pour mon véhicule
                            <ArrowRight aria-hidden='true' className='size-4' />
                        </ButtonLink>
                    </div>
                </div>

                <div className='lg:col-span-8'>
                    <ol className='border-b border-border'>
                        {PRICE_FAMILIES.map(family => (
                            <li
                                key={family.ref}
                                className='grid grid-cols-[2.25rem_minmax(0,1fr)] gap-x-3 border-t border-border py-7 sm:grid-cols-[3rem_minmax(0,1fr)_auto] sm:gap-x-6 lg:py-8'
                            >
                                <span className='label-mono pt-2 text-signal-text'>
                                    {family.ref}
                                </span>

                                <div className='min-w-0'>
                                    <h3 className='heading text-subtitle'>
                                        {family.name}
                                    </h3>

                                    <p className='mt-2 max-w-[34rem] text-muted-foreground'>
                                        {family.description}
                                    </p>

                                    <ul className='mt-4 flex flex-wrap gap-x-5 gap-y-1.5 text-sm'>
                                        {family.includes.map(item => (
                                            <li key={item} className='flex items-center gap-1.5'>
                                                <Check aria-hidden='true' className='size-3.5 shrink-0 text-signal-text' />
                                                {item}
                                            </li>
                                        ))}
                                    </ul>
                                </div>

                                <div className='col-start-2 mt-5 flex flex-wrap items-end justify-between gap-x-6 gap-y-3 sm:col-start-3 sm:row-start-1 sm:mt-0 sm:flex-col sm:flex-nowrap sm:items-end sm:justify-start sm:text-right'>
                                    <dl className='space-y-2'>
                                        {family.options.map(option => {
                                            const service = getService(option.serviceId)

                                            return (
                                                <div key={option.serviceId}>
                                                    <dt className={family.options.length > 1 ? 'text-sm text-muted-foreground' : 'sr-only'}>
                                                        {option.label}
                                                    </dt>

                                                    <dd className='flex items-baseline gap-1.5 sm:justify-end'>
                                                        {service?.free ? (
                                                            <span className='heading text-[1.75rem] leading-tight'>
                                                                Offert
                                                            </span>
                                                        ) : (
                                                            <>
                                                                <span className='text-sm text-muted-foreground'>dès</span>

                                                                <span className='heading text-[1.75rem] leading-tight tabular-nums'>
                                                                    {formatEuro(getFromPrice(option.serviceId))}
                                                                </span>
                                                            </>
                                                        )}
                                                    </dd>
                                                </div>
                                            )
                                        })}
                                    </dl>

                                    <div className='flex items-center gap-4 sm:flex-col sm:items-end sm:gap-2'>
                                        <p className='flex items-center gap-1.5 whitespace-nowrap text-sm text-muted-foreground'>
                                            <Clock aria-hidden='true' className='size-3.5' />
                                            {family.duration}
                                        </p>

                                        <Button
                                            variant='ghost'
                                            onClick={() => onEstimate(family.estimateId)}
                                            className='-mr-3 px-3 text-signal-text hover:text-signal-text'
                                        >
                                            Estimer
                                            <span className='sr-only'> le forfait {family.name.toLowerCase()}</span>
                                            <ArrowRight aria-hidden='true' className='size-4' />
                                        </Button>
                                    </div>
                                </div>
                            </li>
                        ))}
                    </ol>

                    <Reveal className='mt-10 grid gap-8 sm:grid-cols-2'>
                        {GUARANTEES.map(({ icon: Icon, title, text }) => (
                            <div key={title} className='flex gap-4'>
                                <Icon aria-hidden='true' className='mt-0.5 size-6 shrink-0 text-signal-text' />

                                <div>
                                    <h3 className='font-semibold'>
                                        {title}
                                    </h3>

                                    <p className='mt-1.5 text-sm leading-relaxed text-muted-foreground'>
                                        {text}
                                    </p>
                                </div>
                            </div>
                        ))}
                    </Reveal>
                </div>
            </Container>
        </section>
    )
}
